/**
 * E2EE Crypto Utils - AES-256-GCM com Web Crypto API
 * IMPORTANTE: A chave é derivada da senha do usuário.
 * Se o usuário perder a senha, os dados são IRRECUPERÁVEIS.
 */

// Namespace do salt PBKDF2. O identificador concatenado (abaixo) já torna o
// salt único por usuário/tenant — este prefixo só evita colisão entre esquemas.
const SALT_PREFIX = "cofre-e2ee-v1-";

// Tenants "wrapped_key" (legado) derivam o salt de wrap/unwrap a partir do email;
// "wrapped_key_v2" usa o user_id (estável, não muda se o email for alterado).
// As duas listas abaixo mantêm as chamadas de wrap/unwrap consistentes com o modo
// em que o tenant foi criado — trocar o identificador de um tenant já existente
// corrompe o unwrap e torna o cofre IRRECUPERÁVEL.
export const WRAPPED_KEY_MODES = ["wrapped_key", "wrapped_key_v2"];
export const isWrappedKeyMode = (cryptoMode) => WRAPPED_KEY_MODES.includes(cryptoMode);
export const wrapIdentifierFor = (cryptoMode, identity) =>
  cryptoMode === "wrapped_key_v2" ? identity.id : identity.email;

/**
 * Deriva uma chave AES-256 da senha do usuário usando PBKDF2.
 * `identifier` é o email (modo "wrapped_key"/legado) ou o user_id (modo "wrapped_key_v2").
 */
export async function deriveKey(password, identifier) {
  const encoder = new TextEncoder();
  const salt = encoder.encode(SALT_PREFIX + identifier.toLowerCase());
  
  // Importa a senha como chave base
  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    encoder.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );
  
  // Deriva a chave AES-256-GCM
  const key = await window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: salt,
      iterations: 100000,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
  
  return key;
}

/**
 * Deriva a chave do cofre compartilhado a partir da senha do tenant.
 */
export async function deriveTenantKey(password, tenantId) {
  const encoder = new TextEncoder();
  const salt = encoder.encode(`${SALT_PREFIX}tenant-${tenantId}`);

  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    encoder.encode(password),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  return window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: 100000,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function generateVaultKey() {
  return window.crypto.subtle.generateKey(
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
}

export async function exportVaultKey(key) {
  const rawKey = await window.crypto.subtle.exportKey("raw", key);
  const keyArray = new Uint8Array(rawKey);
  return btoa(String.fromCharCode(...keyArray));
}

export async function importVaultKey(rawKeyBase64) {
  const rawKey = Uint8Array.from(atob(rawKeyBase64), c => c.charCodeAt(0));
  return window.crypto.subtle.importKey(
    "raw",
    rawKey,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
}

export async function wrapVaultKey(vaultKey, password, identifier) {
  const wrappingKey = await deriveKey(password, identifier);
  const rawKeyBase64 = await exportVaultKey(vaultKey);
  const encoder = new TextEncoder();
  const iv = window.crypto.getRandomValues(new Uint8Array(12));

  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    wrappingKey,
    encoder.encode(rawKeyBase64)
  );

  const encryptedArray = new Uint8Array(encryptedBuffer);
  return {
    encrypted_key: btoa(String.fromCharCode(...encryptedArray)),
    iv: btoa(String.fromCharCode(...iv)),
  };
}

export async function unwrapVaultKey(wrappedKey, password, identifier) {
  const wrappingKey = await deriveKey(password, identifier);
  const decoder = new TextDecoder();
  const encryptedArray = Uint8Array.from(atob(wrappedKey.encrypted_key), c => c.charCodeAt(0));
  const ivArray = Uint8Array.from(atob(wrappedKey.iv), c => c.charCodeAt(0));

  const decryptedBuffer = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv: ivArray },
    wrappingKey,
    encryptedArray
  );

  return importVaultKey(decoder.decode(decryptedBuffer));
}

/**
 * Criptografa dados usando AES-256-GCM
 * @returns {{ encrypted_payload: string, iv: string }}
 */
export async function encrypt(data, key) {
  const encoder = new TextEncoder();
  const iv = window.crypto.getRandomValues(new Uint8Array(12)); // 96 bits IV para GCM
  
  const jsonString = JSON.stringify(data);
  const encodedData = encoder.encode(jsonString);
  
  const encryptedBuffer = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv: iv },
    key,
    encodedData
  );
  
  // Converte para base64
  const encryptedArray = new Uint8Array(encryptedBuffer);
  const encrypted_payload = btoa(String.fromCharCode(...encryptedArray));
  const ivBase64 = btoa(String.fromCharCode(...iv));
  
  return { encrypted_payload, iv: ivBase64 };
}

/**
 * Descriptografa dados usando AES-256-GCM
 */
export async function decrypt(encrypted_payload, iv, key) {
  try {
    const decoder = new TextDecoder();
    
    // Converte de base64
    const encryptedArray = Uint8Array.from(atob(encrypted_payload), c => c.charCodeAt(0));
    const ivArray = Uint8Array.from(atob(iv), c => c.charCodeAt(0));
    
    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv: ivArray },
      key,
      encryptedArray
    );
    
    const jsonString = decoder.decode(decryptedBuffer);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error("Erro ao descriptografar:", error);
    throw new Error("Falha na descriptografia - chave incorreta ou dados corrompidos");
  }
}

/**
 * Armazena a chave de forma segura na sessão
 * NOTA: Em uma implementação real, considere usar IndexedDB com proteção adicional
 */
export function storeKey(key, tenantId = null) {
  // A chave em si é mantida no CryptoKey que não pode ser exportada.
  window.__cofreKey = key;
  if (tenantId) {
    window.__cofreTenantKeys = window.__cofreTenantKeys || {};
    window.__cofreTenantKeys[tenantId] = key;
  }
}

/**
 * Recupera a chave da sessão
 */
export function getStoredKey() {
  return window.__cofreKey || null;
}

export function getStoredKeyForTenant(tenantId) {
  return tenantId ? window.__cofreTenantKeys?.[tenantId] || null : getStoredKey();
}

/**
 * Limpa a chave da sessão
 */
export function clearKey() {
  window.__cofreKey = null;
  window.__cofreTenantKeys = {};
}

export function clearCurrentKey() {
  window.__cofreKey = null;
}

/**
 * Verifica se a criptografia está disponível
 */
export function isCryptoAvailable() {
  try {
    return !!(window.crypto && window.crypto.subtle && window.crypto.getRandomValues);
  } catch (e) {
    return false;
  }
}
